/**
 * RFC-150: MarketingCrosslinks — small cards (eyebrow + title + arrow) that
 * link from one marketing route to another. Builds the topical-authority graph
 * RFC-149 left out. Renders `<Link to="/...">` (same-origin SPA navigation, no
 * UTM). Carries `data-marketing-link="crosslink"` so the cohesion contract
 * test can count inbound links per page.
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { MotionReveal } from './MotionReveal';

export interface CrosslinkItem {
  /** Internal route, e.g. "/features". */
  to: string;
  /** Short kicker above the title, e.g. "Features". */
  eyebrow: string;
  /** Card title, full sentence ok. */
  title: string;
}

interface MarketingCrosslinksProps {
  /** Optional kicker above the strip, e.g. "Keep exploring". */
  kicker?: string;
  /** Optional title above the strip. */
  title?: string;
  items: readonly CrosslinkItem[];
  className?: string;
}

export function MarketingCrosslinks({
  kicker = 'Keep exploring',
  title,
  items,
  className,
}: MarketingCrosslinksProps) {
  return (
    <section className={`container mx-auto px-4 py-12 md:py-16 ${className ?? ''}`}>
      <div className="max-w-5xl mx-auto">
        {(kicker || title) && (
          <header className="mb-6 text-center">
            {kicker ? (
              <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">{kicker}</p>
            ) : null}
            {title ? <h2 className="text-2xl md:text-3xl font-bold">{title}</h2> : null}
          </header>
        )}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item, i) => (
            <MotionReveal key={item.to} delayMs={i * 60}>
              <Link
                to={item.to}
                data-marketing-link="crosslink"
                className="group flex h-full flex-col justify-between rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:border-primary/40 hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary">{item.eyebrow}</p>
                  <p className="mt-2 text-base font-semibold text-foreground">{item.title}</p>
                </div>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  Read more
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            </MotionReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
